import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE } from "./_shared.js";

/**
 * `READ-PEOPLE-MINE` (@UC0207) — đọc NGƯỜI & CỦA TÔI: thành viên nhóm (ai giữ vai gì) · nhóm của tôi · khoản tôi phải đóng ·
 * việc đang chờ tôi. Tier `read`. Executor `peopleRead.mine` — 0023 bind: `GroupsService.listMemberRefs` ·
 * `GroupsService.listMyGroups` · `PendingDuesService.list` · hộp việc chờ (phiếu chưa bỏ, khai báo chờ xác nhận).
 * 🔴 US-AIV2-tourguide-0011 (@UC0371 · QĐ PO D3): chỉ số `việc chờ tôi` (ví dụ "tôi cần làm gì tiếp theo") ⇒ kết quả `asked_next` ⇒ câu trả lời là
 * lượt `ASKED_NEXT` của Người dẫn đường (cùng thẻ với lời chào). `journey` KHÔNG vào prompt ⇒ đổi kết quả không làm cũ bản ghi eval (đo: 0 ca).
 */
export const READ_PEOPLE_MINE = {
  id: "READ-PEOPLE-MINE",
  uc: "@UC0207",
  tier: "read",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  slots: [
    {
      id: "chiSo",
      type: "enum",
      required: true,
      ask: "Bạn muốn xem: thành viên nhóm, nhóm của bạn, khoản bạn phải đóng, hay việc đang chờ bạn?",
      clarify: "Bạn muốn xem thành viên nhóm, nhóm của bạn, khoản phải đóng, hay việc đang chờ?",
      choices: ["thành viên", "nhóm của tôi", "khoản của tôi", "việc chờ tôi"],
      /*
       * 🔴 US-AIV2-0111 C10 (@UC0207) — đo 28/09: "mình còn phải đóng bao nhiêu", "no tien quy", "khoản nào chưa đóng", "cô có phải
       * đóng tiền quỹ không" vào ô này mà không chứa chữ một lựa chọn ⇒ hỏi 4 lựa chọn. Khớp theo TỪ, bỏ dấu; cụm dài nhất thắng.
       * Ba chỉ số còn lại cũng có đồng nghĩa, để mặc định dưới đây không nuốt câu hỏi về NGƯỜI/NHÓM/VIỆC (`read-indicator-synonyms`).
       * READ-PEOPLE-MINE-WRONG-ANSWER (02/10): chữ mà lưới khuôn đóng `fillPeopleIndicatorFromSentence` đưa cho resolver ("ai đang giữ quỹ lớp",
       * "trưởng nhóm là ai"). RPM-NAMES L1 (review #1140): bản đầu khai "giữ quỹ" · "trưởng nhóm" · "chủ nhóm" TRẦN ⇒ câu KHÁC nghĩa có chữ vai
       * ("việc chờ chủ nhóm", "mình giữ quỹ thì phải làm gì") đọc ra `thành viên`. Nay chỉ chữ HỎI về người: "là ai" · "ai làm" · "ai đang/hiện là" ·
       * "ai hiện làm" · "ai đang/hiện giữ" (ca chiều ngược ở `people-question.test.ts`). Đồng nghĩa KHÔNG vào prompt (chỉ `choices` — `prompt.ts`) ⇒
       * 0 hash đổi.
       */
      synonyms: {
        "thành viên": ["những ai", "có ai", "gồm ai", "bao nhiêu người", "mấy người", "ai là", "ai giữ", "ai đang làm", "vai gì", "quản trị", "thủ quỹ", "người trong nhóm", "là ai", "ai làm", "ai đang là", "ai hiện là", "ai hiện làm", "ai đang giữ", "ai hiện giữ"],
        "nhóm của tôi": ["nhóm nào", "những nhóm", "các nhóm", "mấy nhóm", "tham gia", "quỹ nào", "có chân"],
        "khoản của tôi": ["phải đóng", "phải nộp", "đóng bao nhiêu", "nộp bao nhiêu", "còn nợ", "nợ bao nhiêu", "nợ tiền", "nợ quỹ", "nợ gì", "khoản nào", "khoản phải đóng", "khoản của mình", "chưa đóng", "đóng đủ", "đủ chưa", "đóng chưa", "nợ quỹ nhóm nào"],
        "việc chờ tôi": ["việc gì", "việc nào", "đang chờ", "đang đợi", "chờ tôi", "chờ mình", "cần làm", "bỏ phiếu", "làm gì tiếp"],
      },
      // Mơ hồ ⇒ khoản của tôi: câu hỏi hay gặp nhất của thành viên (đo 28/09: 4/4 câu B01 + R-MULTI · R-ADM đều hỏi khoản phải đóng).
      fallback: "khoản của tôi",
    },
  ],
  llm: {
    description:
      "Xem NGƯỜI trong nhóm và việc của chính mình: nhóm có những ai và bao nhiêu người, ai giữ vai gì, mình đang ở những nhóm nào, còn nợ gì, việc gì đang chờ mình. Đếm hay liệt kê NGƯỜI trong nhóm là ở đây, KHÔNG phải đếm đợt thu (READ-COLLECTIONS) và KHÔNG phải mở màn hình nhóm (GRP-OPEN). Hai ranh giới hay bị nhầm: (1) câu hỏi VỀ VAI mà chỉ XEM (\"ai đang làm quản trị\", \"ai giữ vai gì\", \"ai là thủ quỹ\") là ĐỌC ở đây — GRP-MEMBER-ROLE chỉ dành cho câu ĐỔI vai của ai đó; (2) câu về khoản phải đóng mà chủ ngữ là CHÍNH NGƯỜI HỎI (\"tôi/mình còn nợ gì\", \"khoản của tôi\") là ở đây, còn câu hỏi về CẢ NHÓM hay một ĐỢT THU (\"ai chưa đóng\", \"đợt thu tới đâu\") là READ-COLLECTIONS. Câu HỎI mình (hoặc con mình) đã đóng CHƯA, đóng ĐỦ CHƯA là XEM ở đây; câu BÁO mình vừa chuyển tiền là PAY-REPORT.",
    examples: [
      "toi dang cho duyet o nhom nao",
      "toi dang o nhung nhom nao",
      "tôi đang ở những nhóm nào",
      "toi la thanh vien nao",
      "tôi là thành viên nào",
      "tôi còn nợ bao nhiêu?",
      "toi con no bao nhieu",
      "nhóm có những ai?",
      "nhom co nhung ai",
      "có việc gì đang chờ tôi không?",
      "co viec gi dang cho toi khong",
      "tôi ở những nhóm nào",
      "ai là thủ quỹ",
      "khoản của tôi",
      "việc chờ tôi",
      "mình phải đóng gì tháng này",
      "danh sách thành viên",
      // ——— 0105: thêm cho đủ 40 ———
      "nhóm này có bao nhiêu người",
      "nhom nay co bao nhieu nguoi",
      "ai đang làm quản trị nhóm",
      "ai dang lam quan tri nhom",
      "xem ai giữ vai gì trong nhóm",
      "xem ai giu vai gi trong nhom",
      "liệt kê các nhóm tôi tham gia",
      "liet ke cac nhom toi tham gia",
      "tôi có mấy nhóm quỹ",
      "toi co may nhom quy",
      "cho xem các quỹ mà tôi có chân trong đó",
      "cho xem cac quy ma toi co chan trong do",
      /*
       * 🔴 0105 vòng 2 — CẶP CÂU BỊ THAY (không bị bỏ). Vòng 1 đưa vào `"tôi còn khoản nào chưa đóng
       * không"` + bản không dấu; đo được cả HAI đều rơi sang `READ-COLLECTIONS` (2/3 ca sai của trang).
       *
       * Câu ấy **thật sự** mơ hồ, và mơ hồ ở phía TA chứ không phải phía model: cụm *"khoản nào chưa
       * đóng"* là đúng từ vựng của màn Đợt thu, còn chủ ngữ *"tôi"* đứng đầu câu thì yếu. Thay bằng
       * cặp neo chủ ngữ vào giữa cụm danh từ (*"khoản CỦA TÔI"*), là đúng cách chỉ số `khoản của tôi`
       * của Sổ gọi tên nó — vẫn là câu người Việt gõ thật, chỉ thôi trùng từ vựng với trang bên cạnh.
       */
      "còn khoản nào của tôi chưa đóng không",
      "con khoan nao cua toi chua dong khong",
      "khoản tôi phải nộp là bao nhiêu",
      "khoan toi phai nop la bao nhieu",
      "mình nợ quỹ nhóm nào",
      "minh no quy nhom nao",
      "việc gì đang đợi tôi xử lý",
      "viec gi dang doi toi xu ly",
      "có phiếu nào chờ tôi bỏ phiếu không",
      "co phieu nao cho toi bo phieu khong",
      "tôi cần làm gì tiếp theo",
      "toi can lam gi tiep theo",
      "còn việc nào của tôi chưa xong không",
      // AI-INTERP C1 (@UC0207): HỎI «mình/con mình đóng chưa · đóng đủ chưa» = XEM khoản của chính mình (không phải BÁO đã chuyển — PAY-REPORT).
      "tôi đóng quỹ chưa",
      "toi dong quy chua",
      "mình đã đóng đủ chưa",
      "minh da dong du chua",
      "con tôi đóng quỹ chưa",
      "con toi dong quy chua",
      "bé nhà mình đóng đủ chưa",
      "be nha minh dong du chua",
      "tháng này tôi nộp chưa nhỉ",
      "thang nay toi nop chua nhi",
      // AI-INTERP C5: câu đời thường (xưng hô Nam/Bắc, ck/nè/nghen) — không độn số câu.
      "tui đóng đủ chưa vậy",
      "tui dong du chua vay",
      "cô ơi nhà cháu còn thiếu khoản nào không ạ",
      "co oi nha chau con thieu khoan nao khong a",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "con em đóng chưa ạ",
      "anh đóng đủ chưa em",
      "ko biết đóng chưa",
      "nha chau con no quy dong nao ko a",
      "tui o may nhom vay",
      "co viec gi can tui lam ko",
      "nhom minh co bao nhieu thanh vien z",
    ],
  },
  executor: "peopleRead.mine",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: danh sách người + lịch sử đóng góp của một người. */
  endpoints: [
    "GET /groups/{groupId}/members",
    "GET /groups/{groupId}/people",
    "GET /groups/{groupId}/members/{memberId}/contributions",
  ],
  facts: ["tomTat"],
  templates: {
    reply: "{tomTat}",
  },
  /*
   * LỐI B (QĐ điều phối 19/09). Chỉ số `thành viên` cần một nhóm mà trang CỐ Ý không có ô nhóm; vắng `ctx.groupId` thì câu trả lời nói về
   * **PHẠM VI** ("cần biết bạn hỏi nhóm nào"), KHÔNG nói về sở hữu (bẫy `US-AIV2-0074`). Chip của trang đọc CHỈ đến từ `next` của KẾT QUẢ
   * (US-AIV2-tourguide-0005 · @UC0364) — kết quả `need_group_scope` không có đích là NGÕ CỤT.
   * 🔴 Đo 04/10 (int-spec `ai-doer-people-mine` "thành viên KHÔNG có nhóm hiệu lực"): đích cũ `GRP-OPEN` KHÔNG BAO GIỜ hiện ở đây — ca này chỉ
   * xảy ra khi VẮNG nhóm hiệu lực, mà cổng chỉ mở trang redirect cho phạm vi "tất cả nhóm" trong tập đóng `AI_GROUPLESS_REDIRECT_PAGES`
   * (lời mời · lời xin vào nhóm). Nút develop thực sự hiện là «Báo đã chuyển tiền» / «Xem đợt thu» của "đường suôn" — lạc đề. Đích thật của
   * vế thứ hai của câu ("hoặc hỏi “tôi ở những nhóm nào”") là CHÍNH trang này (mở lại ⇒ chọn chỉ số `nhóm của tôi`).
   */
  journey: {
    outcomes: {
      // vắng nhóm hiệu lực ⇒ câu "mở một nhóm rồi hỏi lại, hoặc hỏi “tôi ở những nhóm nào”" — đích = chính trang này (xem docblock trên).
      need_group_scope: { next: ["READ-PEOPLE-MINE"] },
      // người 0 nhóm ⇒ câu người mới (đã nói "chưa ở nhóm nào" + 3 bước) ⇒ phủ thẻ SETUP-NO-GROUP (nút «Tạo nhóm» của thẻ vẫn giữ); mọi trang
      // khác của trang này đều cần một nhóm người này chưa có ⇒ không mời.
      newcomer: { next: [], covers: ["SETUP-NO-GROUP"] },
      members: { next: ["GRP-OPEN"] },
      my_groups: { next: ["GRP-OPEN"] },
      // «khoản của tôi» có khoản ⇒ câu đã nói số khoản ⇒ phủ thẻ DUTY-MY-UNPAID; «Báo đã chuyển» iff ready() (Q1).
      has_dues: { next: ["PAY-REPORT", "READ-COLLECTIONS"], covers: ["DUTY-MY-UNPAID"] },
      // máy đo K02: 0 khoản ⇒ không mời báo, không mời gì.
      no_dues: { next: [] },
      // 🔴 US-AIV2-tourguide-0011 (@UC0371 · QĐ PO D3): "việc chờ tôi" (= "tôi cần làm gì tiếp") ⇒ câu + nút là lượt `ASKED_NEXT` của Người dẫn
      // đường (`AI_ASKED_NEXT_OUTCOME`) — cùng thẻ, cùng nút đã kiểm với lời chào. Sổ không mời thêm trang nào (nút là của thẻ).
      asked_next: { next: [] },
    },
  },
  help: { title: "Xem thông tin của tôi", tryNow: "tôi còn nợ bao nhiêu?" },
} satisfies AiCapabilityPage;
